import { useBilling } from '../contexts/BillingContext.js';
import { useIsMobileClient } from '../hooks/useTeamsContext.js';

/** Reason a gated action was refused — mirrors the server's 402 `code`. */
export type UpgradeReason = 'trial_expired' | 'quota_exhausted' | 'no_subscription';

interface UpgradeDialogProps {
  reason: UpgradeReason;
  onClose: () => void;
}

/**
 * `mobileBody` exists because Teams mobile forbids upsell: the desktop copy
 * leads with "Subscribe", which is exactly the kind of purchase reference
 * certification policy 114048 rejects. The mobile wording states what still
 * works and points at desktop or web, without asking for money.
 */
const COPY: Record<UpgradeReason, { title: string; body: string; mobileBody: string }> = {
  trial_expired: {
    title: 'Your trial has ended',
    body: 'Subscribe to keep uploading documents and using AI extraction. Documents you have already opened stay available to view.',
    mobileBody:
      'Documents you have already opened stay available to view. To upload and use AI extraction again, open Annoteer on desktop or web.',
  },
  quota_exhausted: {
    title: 'Trial extraction limit reached',
    body: 'The free trial includes 10 AI extractions. Subscribe for unlimited extraction — everything else in your trial keeps working.',
    mobileBody:
      'The free trial includes 10 AI extractions. Everything else in your trial keeps working. To lift the limit, open Annoteer on desktop or web.',
  },
  no_subscription: {
    title: 'Subscription required',
    body: 'An active Annoteer subscription is required to continue.',
    mobileBody:
      'An active Annoteer subscription is required to continue. Open Annoteer on desktop or web to review it.',
  },
};

/**
 * Shown when a gated action returns 402. Deliberately a small inline panel
 * rather than a modal overlay: this renders inside a Teams meeting side panel
 * that can be ~400 px wide, where a centred modal with a backdrop reads as
 * broken and can trap focus during a live meeting.
 *
 * On Teams mobile it degrades to an explanation plus a support link. The user
 * still learns why the action was refused — they just are not sold anything,
 * which is what policy 114048 requires.
 */
export function UpgradeDialog({ reason, onClose }: UpgradeDialogProps) {
  const { startCheckout, isStartingCheckout, actionError, entitlement } = useBilling();
  const isMobile = useIsMobileClient();
  const { title, body, mobileBody } = COPY[reason];

  return (
    <div
      role="alertdialog"
      aria-label={title}
      style={{
        margin: '8px',
        padding: '10px 12px',
        borderRadius: 6,
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border)',
        color: 'var(--color-text)',
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <strong style={{ fontSize: 13 }}>{title}</strong>
        <button
          onClick={onClose}
          aria-label="Dismiss"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            fontSize: 14,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      </div>

      <p style={{ margin: '6px 0 10px', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
        {isMobile ? mobileBody : body}
      </p>

      {/* Price is a payment reference — desktop and web only. */}
      {!isMobile && (
        <p style={{ margin: '0 0 10px', fontWeight: 600 }}>€9 per user / month + VAT</p>
      )}

      {actionError && (
        <p style={{ margin: '0 0 8px', color: '#fca5a5' }} role="alert">
          {actionError}
        </p>
      )}

      {isMobile ? (
        /* Policy 114048 names Support and Contact us as acceptable substitutes
           for a purchase path, so the dead end at least leads somewhere. */
        <a
          href="mailto:support@annoteer.app"
          style={{ color: 'var(--color-text-muted)', fontSize: 11 }}
        >
          Questions? support@annoteer.app
        </a>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => void startCheckout()}
            disabled={isStartingCheckout}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff',
              border: 'none',
              padding: '5px 12px',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
              cursor: isStartingCheckout ? 'default' : 'pointer',
              opacity: isStartingCheckout ? 0.6 : 1,
            }}
          >
            {isStartingCheckout ? 'Opening checkout…' : 'Subscribe'}
          </button>
          <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
            Opens in your browser
          </span>
        </div>
      )}

      {/* "Updating your card" is payment-related, so desktop and web only. */}
      {!isMobile && entitlement?.status === 'past_due' && (
        <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--color-text-muted)' }}>
          Your last payment failed — updating your card may be all that is needed.
        </p>
      )}
    </div>
  );
}
